import React from "react";
import T from "prop-types";
import { reduxForm, Field } from "redux-form";
import { Col } from "react-bootstrap";
import { get } from "lodash";
import { dollars } from "LIB/formatters/currency";
import { vendorRmaUpdate } from "MODULES/RMAsAndWarranties/actions";
import {
  Form as AppForm,
  Check,
  CheckGroup,
  FormRow,
  CurrencyField,
  Select,
  RadioGroupField
} from "MODULES/shared/components/Forms";
import Warning from "MODULES/shared/components/Warning/Warning";
import {
  COST_PER_UNIT,
  ORIGINAL_COST_PER_UNIT
} from "MODULES/inventories/constants";
import { withFieldsFromForm } from "MODULES/shared/decorators";
import { getPartialCreditOptions } from "MODULES/RMAsAndWarranties/components/Detail/Panel/CustomerRmaCreditSection/helpers";
import {
  CREDIT_DENIED,
  CREDIT_ISSUED,
  CREDIT_REQUESTED,
  CUSTOMER_RMA_TYPE,
  PARTIAL_CREDIT_ACCEPTED,
  PARTIAL_CREDIT_AMT,
  PARTIAL_CREDIT_DENIED,
  PARTIAL_CREDIT_STATUS,
  VENDOR_RMA_TYPE
} from "MODULES/RMAsAndWarranties/constants";
import { withFieldAccessors } from "MODULES/shared/decorators/withFieldAccessors";
import {
  PARTIAL_CREDIT,
  RMA_REQUEST_TYPE
} from "MODULES/RepairOrders/constants";
import { TYPE } from "APP_ROOT/constants";
import { RMA } from "MODULES/Receiving/constants";
import validate from "./validate";

function CostWarning({
  creditIssued,
  initialValues: { credit_issued: initialCreditIssued },
  inventory,
  type
}) {
  const costPerUnit = parseFloat(get(inventory, COST_PER_UNIT, 0));
  const originalCostPerUnit = parseFloat(
    get(inventory, ORIGINAL_COST_PER_UNIT, 0)
  );

  if (
    type === VENDOR_RMA_TYPE &&
    inventory &&
    !initialCreditIssued &&
    creditIssued &&
    costPerUnit > originalCostPerUnit
  ) {
    return (
      <Warning>
        The credit issued is over the original purchase price by{" "}
        {dollars(costPerUnit - originalCostPerUnit)}. That amount will be
        applied to a depreciation and a negative commission will be applied to
        buyer/s. Ok to continue?
      </Warning>
    );
  }

  return null;
}

const unreceivedCustomerRma = rma =>
  rma.type === CUSTOMER_RMA_TYPE && !rma.received;

CostWarning.propTypes = {
  creditIssued: T.bool,
  inventory: T.shape({}),
  initialValues: T.shape({
    credit_issued: T.bool,
    partial_credit_status: T.string
  }),
  type: T.string
};

export function Form(props) {
  const {
    credit_issued: creditIssued,
    credit_denied: creditDenied,
    initialValues,
    change,
    partial_credit_status: partialCreditStatus,
    rma,
    rma_request_type: rmaRequestType
  } = props;

  const options = getPartialCreditOptions(partialCreditStatus);
  const finalized =
    initialValues.credit_issued ||
    initialValues.credit_denied ||
    [PARTIAL_CREDIT_ACCEPTED, PARTIAL_CREDIT_DENIED].includes(
      initialValues.partial_credit_status
    );
  const type = get(rma, TYPE);

  const handleCreditRequestedChange = (event, value) => {
    if (!value) {
      change(PARTIAL_CREDIT_STATUS, "requested");
    } else {
      change(PARTIAL_CREDIT_STATUS, null);
      change(PARTIAL_CREDIT_AMT, null);
    }
  };

  const handleRmaRequestTypeChange = (ev, value) => {
    if (value === RMA) {
      change(PARTIAL_CREDIT_STATUS, null);
      change(PARTIAL_CREDIT_AMT, null);
    } else {
      change(CREDIT_REQUESTED, null);
    }
  };

  return (
    <AppForm {...props}>
      <AppForm.Body>
        <CostWarning
          inventory={get(rma, "inventory")}
          creditIssued={creditIssued}
          initialValues={initialValues}
          type={type}
        />
        <FormRow>
          <Col md={12}>
            <Field
              name={RMA_REQUEST_TYPE}
              label="Request Type"
              component={RadioGroupField}
              disabled={finalized}
              onChange={handleRmaRequestTypeChange}
              options={[
                {
                  value: "rma",
                  text: "Request RMA"
                },
                { value: PARTIAL_CREDIT, text: "Request Partial Credit" }
              ]}
            />
          </Col>
        </FormRow>
        {rmaRequestType === RMA && (
          <FormRow>
            <Col md={3}>
              <CheckGroup title="Full Credit Requested?" justified>
                <Field
                  name={CREDIT_REQUESTED}
                  component={Check}
                  label="Yes"
                  icon="fa-check-circle"
                  onChange={handleCreditRequestedChange}
                  disabled={finalized}
                />
              </CheckGroup>
            </Col>
            <Col md={3}>
              <CheckGroup title="Full Credit Issued?" justified>
                <Field
                  name={CREDIT_ISSUED}
                  component={Check}
                  label="Yes"
                  icon="fa-check-circle"
                  disabled={
                    finalized || creditDenied || unreceivedCustomerRma(rma)
                  }
                />
              </CheckGroup>
            </Col>
            <Col md={3}>
              <CheckGroup title="Full Credit Denied?" justified>
                <Field
                  name={CREDIT_DENIED}
                  component={Check}
                  label="Yes"
                  icon="fa-check-circle"
                  disabled={get(rma, "credit_denied") || creditIssued}
                />
              </CheckGroup>
            </Col>
          </FormRow>
        )}
        {rmaRequestType === PARTIAL_CREDIT && (
          <FormRow>
            <Col md={4}>
              <Field
                name={PARTIAL_CREDIT_STATUS}
                component={Select}
                label="Partial Credit Status"
                options={options}
                disabled={finalized}
              />
            </Col>
            <Col md={4}>
              <Field
                required
                name={PARTIAL_CREDIT_AMT}
                component={CurrencyField}
                label="Partial Credit Amt"
                disabled={finalized}
              />
            </Col>
          </FormRow>
        )}
      </AppForm.Body>
    </AppForm>
  );
}

Form.propTypes = {
  credit_issued: T.bool,
  credit_denied: T.bool,
  initialValues: T.shape({
    credit_issued: T.bool,
    partial_credit_status: T.string
  }),
  change: T.func.isRequired,
  partial_credit_status: T.string,
  rma: T.shape({
    type: T.string,
    received: T.bool,
    inventory: T.object,
    sales_item: T.object,
    credit_issued: T.bool,
    credit_denied: T.bool
  }).isRequired,
  setFieldValue: T.func
};

const formName = "rmaCredit";

export default reduxForm({
  form: formName,
  onSubmit: (values, dispatch) => dispatch(vendorRmaUpdate(values)),
  validate
})(
  withFieldsFromForm(formName, [
    CREDIT_DENIED,
    CREDIT_ISSUED,
    PARTIAL_CREDIT_STATUS,
    PARTIAL_CREDIT_AMT,
    RMA_REQUEST_TYPE
  ])(withFieldAccessors(Form))
);



// WEBPACK FOOTER //
// ./app/modules/RMAsAndWarranties/components/Detail/Panel/VendorRmaCreditSection/Form.jsx